import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, Bell, User as UserIcon, LogOut } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { toast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import FullScreenMenu from "@/components/FullScreenMenu";
import NotificationCenter from "@/components/navigation/NotificationCenter";
import UserDashboard from "./dashboard/UserDashboard";
import { notificationService } from "@/services/notificationService";
import { DASHBOARD_DISABLED, isDashboardAllowed } from "@/lib/flags";
import AdvancedUniversalSearch from "@/components/navigation/AdvancedUniversalSearch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NewNavigationProps {
  scrolled?: boolean;
  onOpenDashboard?: (tab: string) => void;
}

const NewNavigation: React.FC<NewNavigationProps> = ({ scrolled = false, onOpenDashboard }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [dashboardTab, setDashboardTab] = useState<string>('profile');
  const [notificationCount, setNotificationCount] = useState(0);
  const { user, isAdmin, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if current path is admin
  const isAdminPage = location.pathname === "/admin";

  // Load notification count
  const loadNotificationCount = useCallback(async () => {
    try {
      let notifications = [];
      if (user) {
        notifications = await notificationService.getUserNotifications(
          user.id,
          isAdmin ? 'admin' : 'user'
        );
      } else {
        notifications = await notificationService.getGuestNotifications();
      }

      const readNotifications = JSON.parse(localStorage.getItem('read_notifications') || '[]');
      const unreadNotifications = notifications.filter(n => !readNotifications.includes(n.id));
      setNotificationCount(unreadNotifications.length);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  }, [user, isAdmin]);

  // Effects for notifications
  useEffect(() => {
    loadNotificationCount();

    let unsubscribe: (() => void) | null = null;
    if (user) {
      notificationService.subscribeToNotifications(
        user.id,
        isAdmin ? 'admin' : 'user',
        (notifications) => {
          const readNotifications = JSON.parse(localStorage.getItem('read_notifications') || '[]');
          const unreadNotifications = notifications.filter(n => !readNotifications.includes(n.id));
          setNotificationCount(unreadNotifications.length);
        }
      );
      unsubscribe = () => notificationService.unsubscribeFromNotifications(user.id);
    }

    return () => {
      unsubscribe?.();
    };
  }, [user, isAdmin, loadNotificationCount]);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
      navigate("/");
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem signing out.",
        variant: "destructive",
      });
    }
  }, [signOut, navigate]);

  const handleOpenDashboard = useCallback((tab: string = 'profile') => {
    if (DASHBOARD_DISABLED && !isDashboardAllowed(user?.email)) {
      toast({ title: "Temporarily unavailable", description: "Account dashboard is disabled right now.", variant: "destructive" });
      return;
    }
    setDashboardTab(tab);
    setIsDashboardOpen(true);
  }, [user]);

  // Expose handleOpenDashboard to parent
  useEffect(() => {
    if (onOpenDashboard) {
      // This is a bit of a hack, but it allows the parent to trigger dashboard opening
      (window as any).__openDashboard = handleOpenDashboard;
    }
    return () => {
      delete (window as any).__openDashboard;
    };
  }, [onOpenDashboard, handleOpenDashboard]);

  return (
    <>
      <header 
        className={cn(
          "w-full border-b border-white/10 bg-slate-950/90 shadow-lg shadow-black/30"
        )}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 lg:h-24">
            
            {/* Logo Section - Always visible except on admin page */}
            <div className="flex items-center flex-shrink-0">
              {!isAdminPage ? (
                <Link 
                  to="/" 
                  className="flex items-center space-x-3"
                >
                  <div className="relative">
                    <div 
                      className="h-10 w-10 lg:h-12 lg:w-12 rounded-full bg-gradient-to-br from-emerald-600 via-teal-600 to-blue-600 flex items-center justify-center shadow-xl border border-white/20"
                      aria-hidden="true"
                    >
                      <span className="text-white font-light text-lg lg:text-xl">F</span>
                    </div>
                  </div>
                  <div className="hidden sm:block">
                    <span className="text-lg lg:text-xl font-light text-white leading-tight tracking-wide">
                      Florida Highwaymen
                    </span>
                    <span className="block text-xs lg:text-sm text-emerald-300 font-light tracking-wider">
                      Auction LLC
                    </span>
                  </div>
                </Link>
              ) : (
                <div className="h-10 lg:h-12" />
              )}
            </div>

            {/* Professional Action Buttons */}
            <div className="flex items-center space-x-3">
              {/* Universal Search */}
              <div className="hidden md:block">
                <AdvancedUniversalSearch />
              </div>
              {/* Notifications */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsNotificationOpen(true)}
                className="relative p-3 rounded-xl border border-transparent backdrop-blur-sm"
              >
                <Bell className="h-5 w-5 text-white/80" />
                {notificationCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-gradient-to-r from-emerald-500 to-teal-500 border-0"
                  >
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </Badge>
                )}
              </Button>

              {/* Account Dashboard */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenDashboard('profile')}
                       className="flex items-center space-x-2 px-4 py-3 rounded-xl text-blue-300 border border-blue-400/30 backdrop-blur-sm font-light tracking-wide"
              >
                <UserIcon className="h-4 w-4" />
                <span className="hidden sm:inline font-light tracking-wide">Account Dashboard</span>
              </Button>

              {/* User Menu (removed Account button per request) */}
              {user ? (
                <div className="flex items-center space-x-2">
                  {isAdmin && (
                    <Link 
                      to="/admin"
                             className="px-4 py-3 rounded-xl text-sm font-light tracking-wide backdrop-blur-sm text-blue-300 border border-blue-400/30"
                    >
                      Admin
                    </Link>
                  )}
                </div>
              ) : (
                <Link to="/login">
                         <Button
                           variant="outline"
                           className="px-6 py-3 rounded-xl border-white/20 text-blue-600 font-light tracking-wide backdrop-blur-sm"
                         >
                    Login
                  </Button>
                </Link>
              )}

              {/* Professional Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMenuToggle}
                className="p-3 rounded-xl border border-transparent backdrop-blur-sm"
              >
                <Menu className="h-6 w-6 text-white/80" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Full Screen Menu with Smooth Animations */}
      <FullScreenMenu 
        isOpen={isMenuOpen} 
        onClose={handleMenuClose} 
      />

      {/* Notification Center */}
      <NotificationCenter 
        isOpen={isNotificationOpen} 
        onClose={() => setIsNotificationOpen(false)} 
        onNotificationsChange={loadNotificationCount}
      />

      {/* User Dashboard */}
      {(!DASHBOARD_DISABLED || isDashboardAllowed(user?.email)) && (
        <UserDashboard 
          isOpen={isDashboardOpen} 
          onClose={() => setIsDashboardOpen(false)} 
          initialTab={dashboardTab}
        />
      )}
    </>
  );
};

export default NewNavigation; 